import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import ScrollToPlugin from "gsap/ScrollToPlugin";
import { toggleAutoplay } from './toggleAutoplay.js';

// MorphSVGPlugin を利用してロゴをモーフィングさせるために登録
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, MorphSVGPlugin);

// ロゴモーフィング用のタイムラインを格納
export const morphTimelines = [];

// ================================
// アニメーション全般を管理するクラス
// ================================
export class Animate {
    constructor() {
        // フェードイン対象要素と方向指定
        this.fadeElements = [
            { selector: '.js-animate_lr', x: -100 }, // 左から右へ
            { selector: '.js-animate_rl', x: 100 },  // 右から左へ
            { selector: '.js-animate_tb', y: 100 },  // 下から上へ
            { selector: '.js-animate_bt', y: -100 }, // 上から下へ
        ];

        this._init(); // 初期化処理
    }

    // 初期化で呼び出す処理まとめ
    _init() {
        this._createMorphTimelines();   // ロゴのモーフィング
        this._fadeShow();               // 汎用フェードイン
        this._setInitialVisibility();   // 初期表示の調整
        this._animateText();            // 文字アニメーション
        this._animateImages();          // 画像アニメーション
        this._setupVideoControls();     // 動画の再生/停止制御
        this._setupScrollTrigger();     // スクロール制御
    }

    // ================================
    // ロゴのモーフィングタイムライン生成
    // ================================
    _createMorphTimelines() {
        // PC 用ロゴセット
        const morphTargetsSet = [
            ['#logo_shigeo_pc_0', '#logo_keiichi_pc_0', '#logo_kurokawa_pc_0'],
            ['#logo_shigeo_pc_1', '#logo_keiichi_pc_1', '#logo_kurokawa_pc_1'],
            ['#logo_shigeo_pc_2', '#logo_keiichi_pc_2', '#logo_kurokawa_pc_2'],
            ['#logo_shigeo_pc_3', '#logo_keiichi_pc_3', '#logo_kurokawa_pc_3'],
            ['#logo_shigeo_pc_4', '#logo_keiichi_pc_4', '#logo_kurokawa_pc_4'],
            ['#logo_shigeo_pc_5', '#logo_keiichi_pc_5', '#logo_kurokawa_pc_5'],
            ['#logo_shigeo_pc_6', '#logo_keiichi_pc_6', '#logo_kurokawa_pc_6'],
            ['#logo_shigeo_pc_7', '#logo_keiichi_pc_7', '#logo_kurokawa_pc_7'],
            ['#logo_shigeo_pc_8', '#logo_keiichi_pc_8', '#logo_kurokawa_pc_8'],
            ['#logo_shigeo_pc_9', '#logo_keiichi_pc_9', '#logo_kurokawa_pc_9'],
        ];

        // PCロゴのモーフィング処理
        morphTargetsSet.forEach((morphTargets, i) => {
            const targetClass = `.logo_def_pc_${i}`;
            const timeline = gsap.timeline({ repeat: -1, yoyo: true }); // 無限ループ、往復

            timeline.to({}, { duration: 1 }); // 最初のウェイト

            morphTargets.forEach(morphTarget => {
                timeline
                    .to(targetClass, {
                        duration: 2,
                        morphSVG: { shape: morphTarget, type: 'linear' }, // 形状変化
                    })
                    .to({}, { duration: 1 }); // 各変化後にウェイト
            });

            morphTimelines.push(timeline);
        });

        // SP 用ロゴセット
        const morphTargetsSetSp = [
            ['#logo_shigeo_sp_0', '#logo_keiichi_sp_0', '#logo_kurokawa_sp_0'],
            ['#logo_shigeo_sp_1', '#logo_keiichi_sp_1', '#logo_kurokawa_sp_1'],
            ['#logo_shigeo_sp_2', '#logo_keiichi_sp_2', '#logo_kurokawa_sp_2'],
            ['#logo_shigeo_sp_3', '#logo_keiichi_sp_3', '#logo_kurokawa_sp_3'],
            ['#logo_shigeo_sp_4', '#logo_keiichi_sp_4', '#logo_kurokawa_sp_4'],
            ['#logo_shigeo_sp_5', '#logo_keiichi_sp_5', '#logo_kurokawa_sp_5'],
            ['#logo_shigeo_sp_6', '#logo_keiichi_sp_6', '#logo_kurokawa_sp_6'],
            ['#logo_shigeo_sp_7', '#logo_keiichi_sp_7', '#logo_kurokawa_sp_7'],
            ['#logo_shigeo_sp_8', '#logo_keiichi_sp_8', '#logo_kurokawa_sp_8'],
            ['#logo_shigeo_sp_9', '#logo_keiichi_sp_9', '#logo_kurokawa_sp_9'],
        ];

        // SPロゴのモーフィング処理
        morphTargetsSetSp.forEach((morphTargets, i) => {
            const targetClass = `.logo_def_sp_${i}`;
            const timeline = gsap.timeline({ repeat: -1, yoyo: true });

            timeline.to({}, { duration: 1 });

            morphTargets.forEach(morphTarget => {
                timeline
                    .to(targetClass, {
                        duration: 2,
                        morphSVG: { shape: morphTarget, type: 'linear' },
                    })
                    .to({}, { duration: 1 });
            });

            morphTimelines.push(timeline);
        });

        // 再生・停止ボタン制御
        toggleAutoplay('.js-play_toggle03', (event) => {
            const isStopped = $(event.currentTarget).hasClass('is-stop');
            this._toggleVideoPlayback('.js-video01', isStopped); // 動画制御
            $('.js-play_toggle_text').text(isStopped ? '再生' : '停止'); // ボタン表示更新
            morphTimelines.forEach(timeline => timeline[isStopped ? 'pause' : 'play']()); // ロゴアニメ制御
        });
    }

    // 初期表示の透明度をセット（FOUT防止）
    _setInitialVisibility() {
        gsap.set(['.heading10', '.hero_area_item01_text01', '.page_ttl'], { autoAlpha: 1 });
    }

    // フェードイン処理（左右上下の動きあり）
    _fadeShow() {
        this.fadeElements.forEach(element => {
            this._applyAnimation($(element.selector), {
                autoAlpha: 0,
                x: element.x || 0,
                y: element.y || 0,
            }, {
                autoAlpha: 1,
                x: 0,
                y: 0,
                duration: 1,
                ease: 'power1.inOut',
            });
        });
    }

    // 文字のアニメーション（1文字ずつ上にフェード）
    _animateText() {
        $('.heading10, .hero_area_item01_text01, .page_ttl').each((_, element) => {
            const $element = $(element);
            const $subElement = $element.find('.heading10_sub');
            const subElementHtml = $subElement.length ? $subElement[0].outerHTML : '';

            // サブ要素を除いたテキスト部分を1文字ごとに分割してspanで囲む
            const htmlContent = $element.html().replace(subElementHtml, '');
            const newHtml = htmlContent.replace(/(<br\s*\/?>)|([^<]+(?:<(?!br\s*\/?>)[^<]*)*)/gi, (match, brTag, text) => {
                if (brTag) return brTag;
                if (text) {
                    return text.split('').map(char => char === ' ' ? ' ' : `<span class="js-text_up">${char}</span>`).join('');
                }
                return '';
            });

            $element.html(newHtml + subElementHtml);

            // 初期状態を透明・下にオフセット
            gsap.set('.js-text_up', { autoAlpha: 0, y: 20 });
            // アニメーション実行
            this._applyAnimation($element.find('.js-text_up'), {
                autoAlpha: 0,
                y: 20,
            }, {
                autoAlpha: 1,
                y: 0,
                stagger: 0.1, // 1文字ごとに順番
                delay: 0.1,
                ease: 'cubic-bezier(0.62,0.05,0.01,0.99)',
            }, $element);
        });
    }

    // 画像アニメーション（クリップパスを使って下から表示）
    _animateImages() {
        $('img').not('.hero_area img, .img_not_animate, .block14_item_img img, .not_img_animation, .swiper-slide img').each((_, img) => {
            const $img = $(img);
            this._applyAnimation($img, {
                clipPath: "inset(0 0 100% 0)", // 下から非表示
            }, {
                clipPath: "inset(0 0 0% 0)",  // 下から徐々に表示
                duration: 1,
                ease: "power2.inOut",
            }, $img);
        });
    }

    // 共通アニメーション適用（scrollTrigger 対応）
    _applyAnimation(target, fromVars, toVars, triggerElement = null) {
        const scrollTrigger = triggerElement ? {
            trigger: triggerElement,
            start: 'center bottom',
            end: 'bottom top',
        } : null;

        gsap.fromTo(target, fromVars, { ...toVars, scrollTrigger });
    }

    // 動画の再生・停止制御
    _setupVideoControls() {
        this._toggleVideoPlayback('.js-video01', false);
        this._toggleVideoPlayback('.js-video02', false);

        toggleAutoplay('.js-play_toggle04', (event) => {
            const isStopped = $(event.currentTarget).hasClass('is-stop');
            this._toggleVideoPlayback('.js-video02', isStopped);
            $('.js-play_toggle_text02').text(isStopped ? '再生' : '停止');
        });
    }

    // スクロール位置によって停止ボタンの位置を変える
    _setupScrollTrigger() {
        ScrollTrigger.create({
            trigger: '.js-play_toggle_stop_section',
            start: 'top bottom',
            onEnter: () => $('.message_stop_btn').css('position', 'absolute'),
            onLeaveBack: () => $('.message_stop_btn').css('position', 'fixed'),
        });
    }

    // video タグの play/pause を切り替え
    _toggleVideoPlayback(selector, isStopped) {
        const method = isStopped ? 'pause' : 'play';
        $(selector).each(function () {
            this[method]();
        });
    }
}
